import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityMessageSegmentDto } from 'api/activity-feed';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

/**
 * Destination d'un lien de segment selon son entityType. `riskId` = dossier porteur de
 * l'événement (contexte de la ligne), utilisé quand l'entité vit dans un dossier.
 * `borrowerProjectId` présent = dossier EMPRUNTEUR : URLs borrower (projet + risque),
 * même mécanique que la recherche globale. Un entityType inconnu (ou sans destination
 * possible) est rendu en texte simple.
 */
const segmentUrl = (
  segment: ActivityMessageSegmentDto,
  riskId?: string,
  borrowerProjectId?: string
): string | null => {
  const id = segment.entityId;
  if (!id) return null;
  // Dossier porteur : espace emprunteur si projet d'emprunt, sinon résolveur par risque.
  const dossierUrl = (targetRiskId?: string) =>
    borrowerProjectId
      ? `/simulation/dossier-emprunteur/${borrowerProjectId}${targetRiskId ? `/${targetRiskId}` : ''}`
      : targetRiskId
        ? `/simulation/dossier/${targetRiskId}`
        : null;
  switch (segment.entityType) {
    case 'RisqueAssure':
      return dossierUrl(id);
    case 'Devis':
      if (!riskId) return null;
      return borrowerProjectId
        ? `/simulation/contractualisation/borrower/${borrowerProjectId}/${riskId}/${id}`
        : `/simulation/contractualisation/${riskId}/${id}`;
    case 'Contrat':
      return `/mes-contrats/details/${id}`;
    case 'Personne':
    case 'Locataire':
    case 'Garant':
    case 'Emprunteur':
      return `/mes-clients/${id}`;
    case 'Note':
      return `/mes-notes?noteId=${id}`;
    case 'ProjetEmprunt':
      return `/simulation/dossier-emprunteur/${id}`;
    // Entités vivant dans un dossier sans page propre : on ouvre le dossier porteur.
    case 'BienImmobilier':
    case 'Mandat':
    case 'CompteBancaire':
    case 'Pret':
      return dossierUrl(riskId);
    default:
      return null;
  }
};

interface ActivitySegmentsProps {
  segments: ActivityMessageSegmentDto[];
  /** Dossier porteur de la ligne (item.riskId) — cible des entités sans page propre. */
  riskId?: string;
  /** Projet d'emprunt porteur (context.borrowerProjectId) — bascule sur les URLs borrower. */
  borrowerProjectId?: string;
  /** Classe du texte plat (hérite par défaut). */
  textClassName?: string;
}

/**
 * Rendu du message riche du flux d'activité : segments text/link concaténés dans
 * l'ordre. Les liens respectent la matrice de droits (ActivityFeedLinks : Courtier) —
 * sinon tout est rendu en texte simple.
 */
export const ActivitySegments: React.FC<ActivitySegmentsProps> = ({
  segments,
  riskId,
  borrowerProjectId,
  textClassName
}) => {
  const navigate = useNavigate();
  const canFollowLinks = useRoleCan(Capability.ActivityFeedLinks);

  return (
    <>
      {segments.map((segment, index) => {
        const url =
          canFollowLinks && segment.type === 'link'
            ? segmentUrl(segment, riskId, borrowerProjectId)
            : null;
        if (!url) {
          return (
            <span key={index} className={textClassName}>
              {segment.text}
            </span>
          );
        }
        return (
          <button
            key={index}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(url);
            }}
            className="font-medium text-primary hover:text-primary/80 hover:underline"
          >
            {segment.text}
          </button>
        );
      })}
    </>
  );
};

export default ActivitySegments;
